import React, { useMemo, useState, useEffect } from "react";
import {
    Box,
    Stack,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Chip,
    Tooltip,
    Divider,
    Typography,
    Avatar,
    useMediaQuery,
    Card,
    CardContent,
} from "@mui/material";
import { parseISO, isValid as isValidDate } from "date-fns";
import moment from "moment-timezone";
import { useTheme } from "@emotion/react";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PanelOrderDialog from "./PanelOrderDialog";
import PanelDataService from "../../../services/Panel.services";

// Data normalization function to convert API response to expected format
function normalizePanels(data) {
    const toNumber = (val) => {
        if (val === null || val === undefined || val === "") return null;
        const parsed = Number(val);
        return Number.isNaN(parsed) ? null : parsed;
    };

    return data.map((panel, idx) => ({
        id: panel.id || idx + 1,
        work_order_number: panel.work_order_number || "",
        count: panel.count || 0,
        production_schedule: panel.production_schedule || null,

        // Project hierarchy
        project: panel.project || null,
        service: panel.service || null,
        hse: panel.hse || null,

        // Assignment
        assignee: panel.assignee || null,
        created_by: panel.created_by || null,
        updated_by: panel.updated_by || null,

        // Lifecycle dates
        due: panel.due || null,
        is_complete: !!panel.is_complete,
        completed: panel.completed || null,
        created: panel.created || null,
        updated: panel.updated || null,
        checked: panel.checked || null,
        checked_by: panel.checked_by || null,
        is_archived: !!panel.is_archived,
        archived: panel.archived || null,
        archived_by: panel.archived_by || null,

        // Items
        items: Array.isArray(panel.items) ? panel.items : [],
        total_qty: panel.total_qty || 0,

        // Attachments
        qr_code: panel.qr_code || null,
        drawings: Array.isArray(panel.drawings) ? panel.drawings : [],

        // Notes
        comments: panel.comments || "",
    }));
}

// Simple avatar helper for initials
function stringAvatar(name = "") {
    const trimmed = name.trim();
    const first = trimmed.split(" ")[0] || "";
    const initial = first.charAt(0) || "";
    return {
        children: initial,
        sx: {
            bgcolor: "primary.light",
            color: "primary.contrastText",
        },
    };
}

// Due Date / Completion Chip Component
function DueDateChip({ value, completed, completedDate }) {
    const theme = useTheme();

    const currentDate = useMemo(
        () => moment.tz(new Date(), "America/New_York").toDate(),
        []
    );

    // If completed, show completion information
    if (completed) {
        const completedParsed = completedDate ? parseISO(completedDate) : null;

        return (
            <Box
                sx={{
                    width: 120,
                    height: 48,
                    border: 1,
                    borderColor: "success.main",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                }}
            >
                <CheckCircleIcon
                    sx={{
                        fontSize: "1.2rem",
                        mr: 1,
                        color: "success.main",
                        flexShrink: 0,
                    }}
                />
                <Stack
                    spacing={0.2}
                    alignItems="flex-start"
                    sx={{ width: "100%", minHeight: 0 }}
                    justifyContent="center"
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                            color: "success.main",
                        }}
                    >
                        Completed
                    </Typography>
                    <Divider sx={{ width: "100%" }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            fontWeight: 400,
                            fontSize: "0.65rem",
                            lineHeight: 1.1,
                        }}
                    >
                        {completedParsed && isValidDate(completedParsed)
                            ? moment(completedParsed).format("MM/DD/YYYY")
                            : "—"}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // Original due date logic for incomplete items
    const parsed = value ? parseISO(value) : null;
    const delta =
        parsed && isValidDate(parsed)
            ? Math.ceil(
                  (parsed.getTime() - currentDate.getTime()) /
                      (1000 * 3600 * 24)
              )
            : null;

    let dueMessage = "No Due Date";
    if (delta !== null) {
        if (delta === 0) dueMessage = "Today";
        else if (delta === 1) dueMessage = "Tomorrow";
        else if (delta === -1) dueMessage = "Yesterday";
        else if (delta > 1) dueMessage = `In ${delta} days`;
        else dueMessage = `${Math.abs(delta)} days ago`;
    }

    return (
        <Box
            sx={{
                width: 120,
                height: 48,
                border: 1,
                borderColor:
                    delta !== null && delta < 0
                        ? "warning.main"
                        : "primary.main",
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
            }}
        >
            <EventAvailableIcon
                sx={{
                    fontSize: "1.2rem",
                    mr: 1,
                    color:
                        delta !== null && delta < 0
                            ? "warning.main"
                            : "primary.main",
                    flexShrink: 0,
                }}
            />
            <Stack
                spacing={0.2}
                alignItems="flex-start"
                sx={{ width: "100%", minHeight: 0 }}
                justifyContent="center"
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        color:
                            delta !== null && delta < 0
                                ? "warning.main"
                                : "primary.main",
                    }}
                >
                    {dueMessage}
                </Typography>
                <Divider sx={{ width: "100%" }} />
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        fontWeight: 400,
                        fontSize: "0.65rem",
                        lineHeight: 1.1,
                    }}
                >
                    {parsed && isValidDate(parsed)
                        ? moment(parsed).format("MM/DD/YYYY")
                        : "—"}
                </Typography>
            </Stack>
        </Box>
    );
}

// Panel Summary Component (simplified version)
function PanelSummary({ darkState, totalCount, openCount, totalQty, openQty }) {
    return (
        <Card
            variant="outlined"
            sx={{
                mt: 0,
                mb: 1,
                width: "100%",
                border: darkState ? "1.5px solid" : "3px solid",
                borderColor: "primary.main",
                borderRadius: "16px",
            }}
        >
            <CardContent
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                }}
            >
                <Typography
                    variant="h3"
                    color="text.primary"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: "2rem", sm: "2.5rem" },
                        textAlign: "center",
                        textTransform: "uppercase",
                    }}
                >
                    PANELS
                </Typography>
                <Divider sx={{ my: 0.5, width: "100%" }} />
                <Stack
                    direction="row"
                    spacing={{ xs: 2, sm: 4 }}
                    sx={{ mt: 1, mb: 2 }}
                >
                    <Stack alignItems="center">
                        <Typography
                            variant="h6"
                            color="text.primary"
                            sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                        >
                            Total {(totalCount || 0) === 1 ? "Order" : "Orders"}
                        </Typography>
                        <Typography
                            variant="h4"
                            color="text.secondary"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: "1.75rem", sm: "2.125rem" },
                            }}
                        >
                            {totalCount || 0}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                            ({totalQty || 0}{" "}
                            {(totalQty || 0) === 1 ? "panel" : "panels"})
                        </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />

                    <Stack alignItems="center">
                        <Typography
                            variant="h6"
                            color="warning.main"
                            sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                        >
                            Open {(openCount || 0) === 1 ? "Order" : "Orders"}
                        </Typography>
                        <Typography
                            variant="h4"
                            color="warning.main"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: "1.75rem", sm: "2.125rem" },
                            }}
                        >
                            {openCount || 0}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="warning.main"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                            ({openQty || 0}{" "}
                            {(openQty || 0) === 1 ? "panel" : "panels"})
                        </Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

// Main Panel Order List Component
function PanelWorkOrderList({ handleOpenSnackbar, darkState, user, token }) {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [openWorkOrder, setOpenWorkOrder] = useState(false);

    useEffect(() => {
        const fetchPanels = async () => {
            if (!token) return;
            setIsLoading(true);
            try {
                const response = await PanelDataService.getAllPanels(token);
                const normalized = normalizePanels(response.data);
                setItems(normalized);
            } catch (error) {
                console.error("Error fetching panels:", error);
                if (handleOpenSnackbar) {
                    handleOpenSnackbar("error", "Failed to fetch panel orders");
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchPanels();
    }, [token, handleOpenSnackbar]);

    const visibleItems = items.filter((p) => !p.is_archived);
    const incompleteItems = visibleItems.filter((p) => !p.is_complete);

    // Calculate totals
    const totalQty = useMemo(
        () =>
            visibleItems.reduce((sum, p) => {
                return (
                    sum +
                    (p.items || []).reduce(
                        (acc, item) => acc + (Number(item.quantity) || 0),
                        0
                    )
                );
            }, 0),
        [visibleItems]
    );

    const incompleteQty = useMemo(
        () =>
            incompleteItems.reduce((sum, p) => {
                return (
                    sum +
                    (p.items || []).reduce(
                        (acc, item) => acc + (Number(item.quantity) || 0),
                        0
                    )
                );
            }, 0),
        [incompleteItems]
    );

    const incompleteCount = incompleteItems.length;
    const totalCount = visibleItems.length;

    const openWO = (order) => {
        setSelected(order);
        setOpenWorkOrder(true);
    };

    const closeWO = () => {
        setOpenWorkOrder(false);
        setSelected(null);
    };

    const replaceAndSort = (updatedItem) =>
        setItems((prev) => {
            const filtered = prev.filter((p) => p.id !== updatedItem.id);
            return [...filtered, updatedItem].sort((a, b) => {
                if (a.due && b.due) return new Date(a.due) - new Date(b.due);
                if (!a.due) return 1;
                if (!b.due) return -1;
                return 0;
            });
        });

    const markChecked = async (orderId) => {
        if (!token) return;
        try {
            const response = await PanelDataService.toggleChecked(
                orderId,
                token
            );
            const normalized = normalizePanels([response.data])[0];
            replaceAndSort(normalized);
            if (handleOpenSnackbar) {
                handleOpenSnackbar(
                    "success",
                    "Panel order marked as checked and completed"
                );
            }
            closeWO();
        } catch (error) {
            console.error("Error toggling checked:", error);
            if (handleOpenSnackbar) {
                handleOpenSnackbar("error", "Failed to mark panel as checked");
            }
        }
    };

    const markUnchecked = async (orderId) => {
        if (!token) return;
        try {
            const response = await PanelDataService.toggleChecked(
                orderId,
                token
            );
            const normalized = normalizePanels([response.data])[0];
            replaceAndSort(normalized);
            if (handleOpenSnackbar) {
                handleOpenSnackbar(
                    "info",
                    "Panel order unchecked and marked as incomplete"
                );
            }
            closeWO();
        } catch (error) {
            console.error("Error toggling unchecked:", error);
            if (handleOpenSnackbar) {
                handleOpenSnackbar("error", "Failed to uncheck panel order");
            }
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
                px: 0,
                py: { xs: 1, sm: 2, md: 4 },
            }}
        >
            <Box sx={{ mb: 3, width: "100%" }}>
                <PanelSummary
                    darkState={darkState}
                    totalCount={totalCount}
                    openCount={incompleteCount}
                    totalQty={totalQty}
                    openQty={incompleteQty}
                />
            </Box>

            {isLoading && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 2 }}
                >
                    Loading panels...
                </Typography>
            )}

            {!isLoading && visibleItems.length === 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 2 }}
                >
                    No panel orders to display.
                </Typography>
            )}

            {visibleItems.length > 0 && (
                <List
                    sx={{
                        width: "100%",
                        bgcolor: "background.paper",
                        border: darkState ? "1.5px solid" : "3px solid",
                        borderRadius: 2,
                        pt: 0,
                        pb: 0,
                        borderColor: "primary.main",
                        overflowX: "hidden",
                    }}
                >
                    {visibleItems.map((order, i) => {
                        const assigneeName = order.assignee
                            ? order.assignee.full_name ||
                              `${order.assignee.first_name || ""} ${order
                                  .assignee.last_name || ""}`.trim()
                            : "";

                        const itemCount = (order.items || []).length;
                        const qtySum = (order.items || []).reduce(
                            (acc, item) => acc + (Number(item.quantity) || 0),
                            0
                        );

                        // Determine which project target is being used
                        const projectLike =
                            order.project || order.service || order.hse || {};
                        const targetType = order.project
                            ? "Project"
                            : order.service
                            ? "Service"
                            : order.hse
                            ? "HSE"
                            : "";

                        const dueChip = (
                            <DueDateChip
                                value={order.due}
                                completed={order.is_complete}
                                completedDate={order.completed}
                            />
                        );

                        return (
                            <div key={order.id}>
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        !isMobile && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{ pr: 1 }}
                                            >
                                                {dueChip}
                                            </Stack>
                                        )
                                    }
                                >
                                    <ListItemButton
                                        onClick={() => openWO(order)}
                                        sx={{
                                            position: "relative",
                                            overflow: "hidden",
                                            minWidth: 0,
                                            opacity: order.is_complete
                                                ? 0.45
                                                : 1,
                                            bgcolor: order.is_complete
                                                ? "action.hover"
                                                : "background.paper",
                                            borderLeft: order.is_complete
                                                ? 4
                                                : undefined,
                                            borderLeftColor: order.is_complete
                                                ? "success.light"
                                                : undefined,
                                        }}
                                    >
                                        {order.is_complete && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    bottom: 0,
                                                    left: 0,
                                                    width: 4,
                                                    bgcolor: "success.main",
                                                    zIndex: 3,
                                                }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                left: 6,
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                bgcolor: order.is_complete
                                                    ? "success.main"
                                                    : "warning.main",
                                                zIndex: 2,
                                            }}
                                        />

                                        <ListItemText
                                            primaryTypographyProps={{
                                                component: "div",
                                                variant: "subtitle1",
                                                sx: {
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                },
                                            }}
                                            primary={
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ overflow: "hidden" }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        variant="filled"
                                                        color="primary"
                                                        label={
                                                            projectLike.number ||
                                                            "—"
                                                        }
                                                        sx={{ maxWidth: 120 }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={700}
                                                        noWrap
                                                        sx={{ maxWidth: "60%" }}
                                                    >
                                                        {projectLike.name ||
                                                            projectLike.number ||
                                                            order.work_order_number}
                                                    </Typography>
                                                </Stack>
                                            }
                                            secondaryTypographyProps={{
                                                component: "div",
                                            }}
                                            secondary={
                                                <>
                                                    <Stack
                                                        spacing={0.25}
                                                        sx={{
                                                            wordBreak:
                                                                "break-word",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="text.primary"
                                                        >
                                                            WO#:{" "}
                                                            {order.work_order_number ||
                                                                "—"}
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Total Panels:{" "}
                                                            {qtySum}
                                                        </Typography>

                                                        {order.comments && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontStyle:
                                                                        "italic",
                                                                }}
                                                            >
                                                                Note:{" "}
                                                                {order.comments}
                                                            </Typography>
                                                        )}
                                                        {assigneeName ? (
                                                            <Tooltip
                                                                title={`Assigned to ${assigneeName}`}
                                                                disableFocusListener
                                                                disableTouchListener
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        mt: 3,
                                                                        px: 1,
                                                                        py: 0.75,
                                                                        width:
                                                                            "fit-content",
                                                                        alignSelf:
                                                                            "flex-start",
                                                                        border: 1,
                                                                        borderColor:
                                                                            "divider",
                                                                        borderRadius: 1,
                                                                        bgcolor:
                                                                            "background.default",
                                                                        display:
                                                                            "inline-flex",
                                                                        alignItems:
                                                                            "center",
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        spacing={
                                                                            0.75
                                                                        }
                                                                        alignItems="center"
                                                                    >
                                                                        {(() => {
                                                                            const avatarProps = stringAvatar(
                                                                                assigneeName
                                                                            );
                                                                            return (
                                                                                <Avatar
                                                                                    {...avatarProps}
                                                                                    sx={{
                                                                                        ...avatarProps.sx,
                                                                                        width: 28,
                                                                                        height: 28,
                                                                                        fontSize: 12,
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })()}
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            noWrap
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {
                                                                                assigneeName
                                                                            }
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                    {isMobile && (
                                                        <Box sx={{ mt: 2 }}>
                                                            {dueChip}
                                                        </Box>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>

                                {i < visibleItems.length - 1 && (
                                    <Divider
                                        sx={{ borderColor: "primary.main" }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </List>
            )}

            <PanelOrderDialog
                open={openWorkOrder}
                onClose={closeWO}
                order={selected}
                user={user}
                onMarkChecked={markChecked}
                onMarkUnchecked={markUnchecked}
            />
        </Box>
    );
}

export default PanelWorkOrderList;
